import * as React from 'react';
import { CasePhases } from './CasePhases';
import { CaseView } from './CaseView';
import { CaseInformation } from './CaseInformation';
import { PhaseName, StatusTypes } from '@/interfaces';
import { CaseContext } from '@/context/CaseContext';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { casesLawyerService, GET_LAWYER_CASE_DETAILS } from '@/api';
import { usePermissions } from '@/context/PermissionsContext';
import { CardCaseAdministration } from '../case-administration/CardCaseAdministration';
import { getValidCaseId } from '@/utils';

interface CaseDetailsLayoutProps {
	children: React.ReactNode;
	numberPhase?: number;
	namePhase?: PhaseName;
}

export const CaseDetailsLayout: React.FC<CaseDetailsLayoutProps> = ({
	children,
	namePhase,
	numberPhase,
}) => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);
	const { allowed } = usePermissions();
	const [saveStatus, setSaveStatus] = React.useState({
		isSaving: false,
		isError: false,
	});

	const hasPermission = allowed.has('case_admin');

	const isNotInvestigationPhase = numberPhase !== 1;

	const { data: caseLawyerDetails } = useQuery({
		queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
		queryFn: () => casesLawyerService.getCaseDetails(caseId as string),
		enabled: !!caseId,
	});

	const readOnlyCases: StatusTypes[] = [
		'Aborted',
		'Cancelled',
		'Case Aborted',
		'Case Resolved',
		'Resolved Case',
		'Case Unsuccessful',
	];

	const isCaseReadOnly = caseLawyerDetails?.data.progress_name
		? readOnlyCases.includes(caseLawyerDetails?.data.progress_name) ||
		  !hasPermission
		: false;

	const handleSetSaveStatus = React.useCallback(
		(status: Partial<typeof saveStatus>) => {
			setSaveStatus((prev) => ({ ...prev, ...status }));
		},
		[]
	);

	const contextValue = React.useMemo(
		() => ({
			caseDetails: caseLawyerDetails?.data,
			isCaseReadOnly,
			saveStatus,
			setSaveStatus: handleSetSaveStatus,
		}),
		[caseLawyerDetails?.data, isCaseReadOnly, saveStatus, handleSetSaveStatus]
	);

	React.useEffect(() => {
		const container = document.getElementById('global-container');
		if (container) {
			const timeout = setTimeout(() => {
				container.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			}, 200);

			return () => clearTimeout(timeout);
		}
	}, [numberPhase]);

	return (
		<CaseContext.Provider value={contextValue}>
			<CaseView />
			<div className="flex flex-col lg:flex-row gap-[18px] w-full">
				{namePhase && numberPhase ? (
					<>
						<CardCaseAdministration
							numberPhase={numberPhase}
							namePhase={namePhase}
						>
							{children}
						</CardCaseAdministration>
						<div className="space-y-4 flex-none">
							<CasePhases />
							{caseLawyerDetails && isNotInvestigationPhase && (
								<CaseInformation caseDetails={caseLawyerDetails.data} />
							)}
						</div>
					</>
				) : (
					children
				)}
			</div>
		</CaseContext.Provider>
	);
};
